import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Biz360Logo } from "../components/layout/Biz360Logo";
import { QONTO_ENABLED } from "../utils/featureFlags";

/**
 * Page de retour après autorisation Qonto.
 * Qonto redirige vers https://biz360.obusiness.fr/qonto/callback?code=...&state=...
 * On lit le code + state, on appelle POST /api/qonto/oauth/callback,
 * puis on redirige vers Paramètres.
 */
export const QontoCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  // ⚠️ React StrictMode exécute useEffect 2x en dev — un code OAuth Qonto ne peut être consommé qu'1 fois.
  // On garantit l'idempotence avec un ref qui survit aux re-renders.
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) return;
    handledRef.current = true;

    // Partenariat Qonto abandonné définitivement : ne tente jamais l'appel API
    // (le backend renvoie 503 sur /api/qonto/* de toute façon).
    if (!QONTO_ENABLED) {
      setStatus("error");
      setMessage("L'intégration Qonto n'est plus disponible.");
      return;
    }

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");
    const errorDesc = searchParams.get("error_description");

    if (error) {
      setStatus("error");
      setMessage(errorDesc || `Connexion refusée (${error})`);
      return;
    }

    if (!code || !state) {
      setStatus("error");
      setMessage("Paramètres manquants dans la redirection Qonto.");
      return;
    }

    if (!token) {
      setStatus("error");
      setMessage("Connecte-toi d'abord à Biz360 puis relance la connexion Qonto.");
      return;
    }

    const API = process.env.REACT_APP_BACKEND_URL;
    axios
      .post(
        `${API}/api/qonto/oauth/callback`,
        { code, state },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then((res) => {
        setStatus("success");
        setMessage(`Compte Qonto "${res.data.organization_name || ""}" connecté avec succès.`);
        setTimeout(() => navigate("/parametres"), 2000);
      })
      .catch((err) => {
        setStatus("error");
        setMessage(err?.response?.data?.detail || "Erreur lors de la connexion Qonto.");
      });
  }, [searchParams, token, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-background">
      <Biz360Logo />
      <div className="mt-8 max-w-md w-full bg-card border rounded-xl p-6 text-center" data-testid="qonto-callback-page">
        {status === "loading" && (
          <>
            <div className="text-3xl mb-3">🏦</div>
            <h1 className="text-lg font-bold" style={{ color: "#210875" }}>
              Connexion Qonto en cours…
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              On finalise la liaison avec ton compte Qonto, ça prend quelques secondes.
            </p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="text-3xl mb-3">✅</div>
            <h1 className="text-lg font-bold text-green-700">Connecté !</h1>
            <p className="text-sm text-muted-foreground mt-2">{message}</p>
            <p className="text-xs text-muted-foreground mt-3">Redirection vers tes Paramètres…</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="text-3xl mb-3">⚠️</div>
            <h1 className="text-lg font-bold text-red-700">Connexion impossible</h1>
            <p className="text-sm text-muted-foreground mt-2">{message}</p>
            <button
              onClick={() => navigate("/parametres")}
              className="mt-4 px-4 h-10 rounded-lg text-white font-semibold"
              style={{ backgroundColor: "#210875" }}
              data-testid="qonto-callback-back-btn"
            >
              Retour aux Paramètres
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default QontoCallbackPage;